import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Option,
  Select,
  Typography,
} from "@mui/joy";
import { Link } from "react-router-dom";
import { useMeasurements } from "../../hooks/measurements/useMeasurements";
import { apiClient } from "../../lib/api";
import { MaterialTakeoffPanel } from "../measurements/MaterialTakeoffPanel";
import { useJobWorkspace } from "./JobWorkspace";

export function JobMeasurementReview({
  job,
  detailed,
}: {
  job: any;
  detailed: any[];
}) {
  const { data: maps = [], error: mapsError } = useMeasurements(job.id);
  const { data, command, error } = useJobWorkspace(job.id);
  const [left, setLeft] = useState("");
  const [right, setRight] = useState("");
  const sources = [
    ...maps.map((m: any) => ({
      key: `measurement:${m.id}`,
      kind: "measurement",
      record: m,
    })),
    ...detailed.map((m) => ({
      key: `detailed:${m.id}`,
      kind: "detailed",
      record: m,
    })),
  ];
  const chosen = data?.measurement_selection;
  const selected = sources.find(
    (s) => s.kind === chosen?.kind && s.record.id === chosen?.id,
  );
  const { data: mapDetails = [] } = useQuery({
    queryKey: ["selected-measurement-detail", chosen?.id],
    enabled: chosen?.kind === "measurement",
    queryFn: async () =>
      (
        await apiClient.getAllPages<any>(
          `/api/measurements/${chosen?.id}/detailed-measurements/`,
        )
      ).data,
  });
  const selectedDetail =
    selected?.kind === "detailed" ? selected.record : mapDetails[0];
  const sourceLabel = (source: (typeof sources)[number]) =>
    `${source.kind === "detailed" ? source.record.source_type || "Report" : "Map"} #${source.record.id}`;
  return (
    <Card sx={{ mb: 3 }}>
      <Typography level="title-lg">
        Measurement review and takeoff source
      </Typography>
      {(error || mapsError || command.error) && (
        <Alert color="danger">
          {command.error?.message || "Could not load measurement sources."}
        </Alert>
      )}
      <Typography level="body-sm">
        Compare revisions, then select a completed source. The takeoff below
        uses that selection.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {[
          [left, setLeft],
          [right, setRight],
        ].map(([value, set], index) => {
          const source = sources.find((s) => s.key === value);
          const record = source?.record;
          return (
            <Box key={index}>
              <FormControl>
                <FormLabel>
                  {index ? "Replacement measurement" : "Previous measurement"}
                </FormLabel>
                <Select
                  value={String(value)}
                  placeholder="Choose a source"
                  onChange={(_, v) => (set as (v: string) => void)(v || "")}
                >
                  {sources.map((s) => (
                    <Option value={s.key} key={s.key}>
                      {sourceLabel(s)}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              {record && (
                <Box sx={{ mt: 1 }}>
                  <Typography>
                    Source: {record.source_type || "Map measurement"}
                  </Typography>
                  <Typography>
                    Captured:{" "}
                    {(
                      record.measurement_date ||
                      record.image_date ||
                      record.nearmap_survey_date ||
                      record.created_at ||
                      ""
                    ).slice(0, 10) || "Unknown"}
                  </Typography>
                  <Typography>
                    Status: {record.parsing_status || record.status}
                  </Typography>
                  <Typography>
                    Area:{" "}
                    {Number(
                      record.total_area_sqft ?? record.total_area ?? 0,
                    ).toLocaleString()}{" "}
                    sq ft
                  </Typography>
                  <Typography>
                    Pitch: {record.predominant_pitch || "Not recorded"} ·
                    Revision #{record.id}
                  </Typography>
                  <Button
                    sx={{ mt: 1 }}
                    loading={command.isPending}
                    disabled={
                      (record.parsing_status || record.status) !== "completed"
                    }
                    onClick={() =>
                      command.mutate({
                        action: "select_measurement",
                        kind: source!.kind,
                        measurement: record.id,
                      })
                    }
                  >
                    Use this source
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      {selected && (
        <Box>
          <Typography level="title-md" sx={{ my: 2 }}>
            Current: {sourceLabel(selected)}
          </Typography>
          {selected.kind === "measurement" && (
            <Button
              component={Link}
              to={`/operations/measurements/${selected.record.id}`}
              variant="outlined"
            >
              Open selected measurement for estimating
            </Button>
          )}
          {selectedDetail ? (
            <MaterialTakeoffPanel
              key={`${selected.key}:${selectedDetail.id}`}
              measurement={{
                id: selected.record.id,
                address: job.address_1,
                created_at: selected.record.created_at,
              }}
              detailedMeasurement={selectedDetail}
            />
          ) : (
            <Typography level="body-sm">
              Open the selected measurement to finish detailed takeoff
              calculations.
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
}
